import { useEffect, useState } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import {
  findKeyInDID,
  importEd25519Key,
  isWalletDID,
  parseEd25519PrivateKey,
  type IssuerSigner,
} from '../lib/credentialSigning';

interface Props {
  /** The DID that signs. */
  did: string | undefined;
  /** assertionMethod to issue credentials, authentication to present them. */
  relationship?: 'assertionMethod' | 'authentication';
  onChange: (signer: IssuerSigner | null) => void;
}

/**
 * Choose how to sign: the DID's MetaMask wallet, or its Ed25519 key pasted
 * here. The pasted key is checked against the DID document, kept in memory
 * as a non-extractable browser key, and never sent to the server.
 */
export default function IssuerSignerPicker({ did, relationship = 'assertionMethod', onChange }: Props) {
  const { account, signer: walletSigner, connect } = useWeb3();
  const walletDID = isWalletDID(did);
  const walletAddress = walletDID ? did!.split(':').pop()!.toLowerCase() : '';
  const walletMatches = !!account && account.toLowerCase() === walletAddress;
  const [mode, setMode] = useState<'wallet' | 'key'>(walletDID ? 'wallet' : 'key');
  const [keyText, setKeyText] = useState('');
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setMode(walletDID ? 'wallet' : 'key');
    setKeyText('');
    setStatus(null);
  }, [did, walletDID]);

  useEffect(() => {
    if (mode !== 'wallet') return;
    if (walletDID && walletMatches && walletSigner) {
      onChange({ kind: 'wallet', signer: walletSigner });
      setStatus({ ok: true, text: 'MetaMask will ask you to sign each credential.' });
    } else {
      onChange(null);
      setStatus(
        !account
          ? { ok: false, text: 'Connect the wallet that owns this DID.' }
          : { ok: false, text: `The connected wallet is not ${walletAddress}.` },
      );
    }
    // onChange is a setter from the parent; re-running on its identity is not needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, walletDID, walletMatches, walletSigner, account]);

  const checkKey = async () => {
    onChange(null);
    if (!did || !keyText.trim()) {
      setStatus(null);
      return;
    }
    try {
      const key = await importEd25519Key(parseEd25519PrivateKey(keyText));
      const verificationMethod = await findKeyInDID(did, key.publicKey, relationship);
      setStatus({ ok: true, text: `Signing with ${verificationMethod}. The key stays in this browser.` });
      onChange({ kind: 'key', key, verificationMethod });
    } catch (e) {
      setStatus({ ok: false, text: e instanceof Error ? e.message : String(e) });
    }
  };

  if (!did) return null;

  return (
    <div className="space-y-3 rounded-lg border border-border-subtle p-4">
      <p className="text-xs font-bold text-text-muted">Sign as {did}</p>
      <div className="flex flex-wrap gap-4 text-sm">
        {walletDID && (
          <label className="flex items-center gap-2">
            <input id="signer-wallet" type="radio" checked={mode === 'wallet'} onChange={() => setMode('wallet')} />
            MetaMask wallet
          </label>
        )}
        <label className="flex items-center gap-2">
          <input id="signer-key" type="radio" checked={mode === 'key'} onChange={() => { setMode('key'); onChange(null); setStatus(null); }} />
          Ed25519 key (saved when the DID was created)
        </label>
      </div>

      {mode === 'wallet' && walletDID && !account && (
        <button type="button" className="btn-secondary" onClick={() => void connect()}>Connect wallet</button>
      )}

      {mode === 'key' && (
        <textarea
          id="signer-private-key"
          value={keyText}
          onChange={(e) => setKeyText(e.target.value)}
          onBlur={() => void checkKey()}
          rows={2}
          spellCheck={false}
          autoComplete="off"
          placeholder="Paste the private key (hex or base64)"
          className="w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs focus:border-primary focus:outline-none"
        />
      )}

      {status && (
        <p className={status.ok ? 'text-xs text-emerald-700' : 'text-xs text-red-600'}>{status.text}</p>
      )}
    </div>
  );
}
